import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Flex, Grid, GridItem } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { Coin } from 'interfaces/MeshMaven';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useHelpMeWriteTooltip from 'screens/common/useHelpMeWriteTooltip';
import { VisibilityIconPack } from 'screens/home/addPost/AddPostModal/SelectVisibility';
import { checkCFGenAIEnabled } from 'utils/helpers/featureFlags';
import { useClearMutationCache } from 'utils/helpers/reactQueryHelpers';
import uuidv4 from 'utils/uuidv4';

import { CheckCircle, CloseCircle, RabbitCompetency } from 'assets/assetsV1/iconComponents';
import { Objective } from 'assets/assetsV1/iconComponents/Objective';

import {
  ALERT_MESSAGES,
  CONTINUOUS_FEEDBACK_PEER_STATUS,
  FEEDBACK_FORM_VISIBILITY,
  FEEDBACK_FORM_VISIBILITY_RABBIT,
  POST_VISIBILITY_TYPES,
  TOAST_STATUSES,
} from 'constants/ProjectConstants';
import { generateKey, getQueryParams } from 'constants/helper';
import {
  sendOpenedPendingFeedbackEvent,
  sendSharedFeedback,
  sendRespondedToFeedbackRequest,
} from 'constants/mixpanelHelper';
import { sendCFGenAIButtonClickedEvent, CF_GEN_AI_BUTTON_TYPES } from 'constants/mixpanelHelpers/cfGenAIEvents';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { shareRequestedFeedback, acceptFeedback, declineFeedback } from 'services/continuousFeedback';
import { getApiBodyFromEditorStateAndMedia } from 'services/helpers/post';

import { BUTTON_VARIANT, Button } from 'uiToolkitV1/Button';
import { BUTTON_COLORS } from 'uiToolkitV1/Button/Button';
import MavenButton from 'uiToolkitV1/MavenButton/MavenButton';
import { Tooltip } from 'uiToolkitV1/Tooltip';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import FeedbackCompetencies from '../common/FeedbackCompetencies';
import FeedbackRTE from '../common/FeedbackRTE';
import Layout from '../common/Layout';
import MeshMavenTitleComp from '../common/MeshMaven/MeshMavenTitleComp';
import { REACT_QUERY_FEEDBACK_DRAWER_KEYS } from '../constants';
import { useFillRequestedFeedbackQuery } from '../hooks/feedbackDataHook';
import { useFeedbackAnalysisMutation } from '../hooks/meshMavenDataHooks';
import {
  setClearFillRequestedFeedbackData,
  setFillRequestedAttachments,
  setFillRequestedPost,
  setFillRequestedRevisionNumber,
  setFillRequestedAddRevision,
  setFillRequestedSentimentType,
  setMeshMavenGroupId,
  setPrimaryPanelAction,
  setRequestedSecondaryPanelOpen,
  useFillRequestedFeedbackData,
  useRequestedFeedbackData,
  setFillRequestedRephraseError,
  setFillRequestedAnalysisError,
} from '../slice/feedbackSlice';
import { RequestFeedbackDetails } from '../types/RequestedFeedback';

interface Props {
  handleClose: () => void;
  feedbackId: string;
}

const RequestedFeedback = ({ handleClose: handleCloseDrawer, feedbackId }: Props) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const parentRef = useRef(null);
  const history = useHistory();
  const { moduleName } = useOrganisationSettings();
  const { data: requestFeedbackDetails, isLoading } = useFillRequestedFeedbackQuery({
    feedbackId,
  });

  const { review, employee } = requestFeedbackDetails ?? ({} as RequestFeedbackDetails);

  const { receiverEmployeeId, requesterEmployee, visibility, questionText, goal, peerStatus } = review ?? {};

  const { feedbackPost, feedbackSentimentType, attachments, revisions } = useFillRequestedFeedbackData();

  const isNominated = peerStatus === CONTINUOUS_FEEDBACK_PEER_STATUS.NOMINATED;

  const helpMeWriteTooltip = useHelpMeWriteTooltip({
    editorState: feedbackPost,
    checkMention: false,
  });

  const { isSecondaryPanelOpen } = useRequestedFeedbackData();

  const { source, sentiment: sentimentFromQuery } = getQueryParams(history.location.search);

  const [isSaving, setIsSaving] = useState(false);

  const dispatch = useDispatch();

  const { mutate: getFeedbackAnalysis } = useFeedbackAnalysisMutation();

  const clearMutationCache = useClearMutationCache();

  useEffect(() => {
    dispatch(setPrimaryPanelAction({ primaryPanelAction: 'REQUESTED' }));
    dispatch(setMeshMavenGroupId({ meshMavenGroupId: uuidv4() }));
  }, [dispatch]);

  useEffect(() => {
    sendOpenedPendingFeedbackEvent({
      eventProperties: {
        source,
      },
    });
  }, [source]);

  useEffect(() => {
    if (sentimentFromQuery && sentimentFromQuery !== feedbackSentimentType) {
      onFeedbackTypeSelect(sentimentFromQuery);
    }
  });

  const onFeedbackTypeSelect = (type) => {
    dispatch(
      setFillRequestedSentimentType({
        feedbackSentimentType: type,
      })
    );
  };
  const onUpdateEditorState = (editorState) => {
    dispatch(
      setFillRequestedPost({
        feedbackPost: editorState,
      })
    );
  };

  const setCompetency = (editorState) => {
    dispatch(
      setFillRequestedPost({
        feedbackPost: editorState,
      })
    );
  };

  const setImage = (image) => {
    dispatch(
      setFillRequestedAttachments({
        images: [
          {
            type: 'IMAGE',
            url: image?.url,
            uuid: generateKey(4),
          },
        ],
      })
    );
  };

  const setGif = (gif) => {
    dispatch(
      setFillRequestedAttachments({
        gifs: [
          {
            type: 'GIF',
            url: gif,
            uuid: generateKey(4),
          },
        ],
      })
    );
  };

  const onDeleteImage = () => {
    dispatch(
      setFillRequestedAttachments({
        images: [],
      })
    );
  };

  const onClose = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.meshRecommends.nudgeCards]);
    clearMutationCache([REACT_QUERY_KEYS.RephraseText]);
    clearMutationCache([REACT_QUERY_KEYS.FeedbackAnalysis]);
    handleCloseDrawer();
    setTimeout(() => {
      dispatch(setClearFillRequestedFeedbackData());
    }, 500);
  };

  const onSave = async () => {
    setIsSaving(true);
    const { uuid } = review;
    const text = feedbackPost.getCurrentContent().getPlainText('\n');
    if (text.length === 0) {
      toast({
        title: ALERT_MESSAGES.CONTINUOUS_FEEDBACK.SHARE.FAILURE,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      return;
    }
    const data = getApiBodyFromEditorStateAndMedia(feedbackPost, null, false, feedbackSentimentType);

    const media = [...(attachments?.images ?? []), ...(attachments?.gifs ?? [])];

    let body = {
      ...data,
      media,

      // visibility should be only GLOBAL or PRIVATE
      visibilityType:
        review?.visibility === FEEDBACK_FORM_VISIBILITY[4].value
          ? POST_VISIBILITY_TYPES.global.name
          : POST_VISIBILITY_TYPES.private.name,
    };
    if (goal) {
      body = {
        ...body,
        // @ts-ignore
        goal,
      };
    }

    sendSharedFeedback({
      eventProperties: {
        source,
        feedbackType: feedbackSentimentType === 'APPRECIATIVE' ? 'Praise' : 'Advice',
        visibility,
        association: goal,
        competency: !isEmpty(data?.macros?.attributes?.orgTag),
      },
    });

    try {
      await shareRequestedFeedback({
        body,
        feedbackId: uuid,
        is_new_panel: true,
      });
      toast({
        title: ALERT_MESSAGES.CONTINUOUS_FEEDBACK.SHARE.SUCCESS,
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
      onClose();
    } catch (error) {
      console.error('Error sharing feedback ', error);
      toast({
        title: ALERT_MESSAGES.CONTINUOUS_FEEDBACK.SHARE.FAILURE,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    } finally {
      setIsSaving(false);
      queryClient.invalidateQueries({ queryKey: ['CFTable'] });
      queryClient.invalidateQueries({ queryKey: ['CFTabs'] });
      queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.mySummary.mySummaryFeedback] });
    }
  };

  const handleHelpMeWrite = () => {
    const newRevision = {
      requestId: '',
      originalState: feedbackPost,
      updatedState: feedbackPost,
      isAIFeedbackProvided: false,
      isReanalyzeEnabled: false,
      feedbackAnalysis: { context: 0, observations: 0, impact: 0, next_steps: 0 } as Coin,
    };
    sendCFGenAIButtonClickedEvent({
      eventProperties: {
        source,
        type: CF_GEN_AI_BUTTON_TYPES.HELP_ME_WRITE,
      },
    });
    openSecondaryPanel();
    dispatch(
      setFillRequestedAddRevision({
        revision: newRevision,
      })
    );
    dispatch(setFillRequestedRevisionNumber({ revisionNumber: revisions.length }));
    getFeedbackAnalysis({
      revision: newRevision,
      revisionNumber: revisions.length,
      editorState: feedbackPost,
    });
  };

  const openSecondaryPanel = () => {
    dispatch(
      setRequestedSecondaryPanelOpen({
        isSecondaryPanelOpen: true,
      })
    );
  };

  const closeSecondaryPanel = () => {
    dispatch(
      setRequestedSecondaryPanelOpen({
        isSecondaryPanelOpen: false,
      })
    );
    dispatch(
      setFillRequestedRephraseError({
        error: null,
      })
    );
    dispatch(
      setFillRequestedAnalysisError({
        error: null,
      })
    );
    clearMutationCache([REACT_QUERY_KEYS.RephraseText]);
    clearMutationCache([REACT_QUERY_KEYS.FeedbackAnalysis]);
  };

  const handleAccept = useCallback(
    async (e) => {
      e?.stopPropagation();
      try {
        await acceptFeedback({ feedbackId });
        sendRespondedToFeedbackRequest({
          eventProperties: {
            response: 'Accept',
            // source
          },
        });
        queryClient.invalidateQueries({ queryKey: ['CFTable'] });
        queryClient.invalidateQueries({ queryKey: ['CFTabs'] });
        queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.mySummary.mySummaryFeedback] });
        queryClient.invalidateQueries({
          queryKey: [REACT_QUERY_FEEDBACK_DRAWER_KEYS.fillRequestedFeedback, feedbackId],
        });
      } catch (err) {
        console.error('Error accepting : ', err);
      }
    },
    [feedbackId, queryClient]
  );

  const handleReject = useCallback(
    async (e) => {
      e?.stopPropagation();
      try {
        await declineFeedback({ feedbackId });
        sendRespondedToFeedbackRequest({
          eventProperties: {
            response: 'Reject',
            // source
          },
        });
        queryClient.invalidateQueries({ queryKey: ['CFTable'] });
        queryClient.invalidateQueries({ queryKey: ['CFTabs'] });
        queryClient.invalidateQueries({ queryKey: [REACT_QUERY_KEYS.mySummary.mySummaryFeedback] });
        onClose();
      } catch (err) {
        console.error('Error rejecting : ', err);
      }
    },
    [feedbackId, onClose, queryClient]
  );

  const selectedVisibilityOption = FEEDBACK_FORM_VISIBILITY_RABBIT.find((option) => option.value === visibility);

  if (isLoading) {
    return null;
  }

  return (
    <Layout
      header="Requested Feedback"
      onClose={onClose}
      onSave={onSave}
      applicableEmployees={receiverEmployeeId ? [String(receiverEmployeeId)] : []}
      showSecondaryPanel={isSecondaryPanelOpen}
      secondaryPanelAction="MESH_MAVEN"
      secondaryPanelHeader={<MeshMavenTitleComp />}
      onSecondaryPanelClose={closeSecondaryPanel}
      saveButtonDisabled={isSaving || isNominated}
      saveButtonDisabledReason={isNominated ? '' : 'Sharing your feedback'}
    >
      <Box w="100%">
        {/*  @ts-ignore */}
        <Flex flexDir="column" w="100%" background="#EBEFF3" gap="4px">
          <Box p="8px 12px" flexDir="column" w="100%" background="white">
            {/*  @ts-ignore */}
            <Grid
              alignItems="center"
              templateColumns={{
                base: 'repeat(1, 1fr)',
                md: 'repeat(3, 1fr)',
              }}
              gap={0}
              rowGap={4}
              p="12px 0px"
              mb={2}
            >
              {/*  @ts-ignore */}
              <GridItem>
                <Flex alignItems="flexStart" gap={2} flexDir="column">
                  <TextLabel color="text.g2">Requested for : </TextLabel>
                  <TextBodyMedium>{employee?.displayName}</TextBodyMedium>
                </Flex>
              </GridItem>
              <GridItem>
                <Flex alignItems="flexStart" gap={2} flexDir="column">
                  <TextLabel color="text.g2">Requested by : </TextLabel>
                  <TextBodyMedium>{requesterEmployee?.displayName}</TextBodyMedium>
                </Flex>
              </GridItem>
              <GridItem>
                <Flex alignItems="flexStart" gap={2} flexDir="column">
                  <TextLabel mr="8px" color="text.g2">
                    Visibility:
                  </TextLabel>
                  <Box display="flex" alignItems="center">
                    {' '}
                    {VisibilityIconPack[selectedVisibilityOption.icon]}{' '}
                    <TextBodyMedium>{selectedVisibilityOption.label}</TextBodyMedium>
                  </Box>
                </Flex>
              </GridItem>
            </Grid>
            {questionText && (
              <Box padding="4px 0px">
                <TextLabel mb="4px" bold>
                  Request Details
                </TextLabel>
                <TextBodyMedium padding="8px 0px">{questionText}</TextBodyMedium>
              </Box>
            )}
            {goal && (
              <Box padding="4px 0px">
                <TextLabel mb="4px" bold>
                  Related {moduleName}
                </TextLabel>
                <Flex alignItems="center" cursor="pointer">
                  <Objective color="#7651BC" boxSize="18px" mr="8px" />

                  <TextBodyMedium padding="8px 0px">{goal?.text}</TextBodyMedium>
                </Flex>
              </Box>
            )}
            {isNominated && (
              <Box
                display="flex"
                padding="16px"
                justifyContent="flex-end"
                alignItems="center"
                alignSelf="stretch"
                borderRadius="4px"
                background="#F5F7F9"
              >
                <Button
                  StartIcon={CloseCircle}
                  variant={BUTTON_VARIANT.tertiary}
                  color={BUTTON_COLORS.red}
                  onClick={handleReject}
                >
                  Deny
                </Button>
                <Button StartIcon={CheckCircle} variant={BUTTON_VARIANT.outlined} onClick={handleAccept}>
                  Accept
                </Button>
              </Box>
            )}
          </Box>

          <Box p="8px 12px" flexDir="column" w="100%" background="white">
            <Box
              ref={parentRef}
              mt="4px"
              pos="relative"
              pointerEvents={isNominated ? 'none' : 'all'}
              opacity={isNominated ? '0.08' : 1}
            >
              {visibility === 'PUBLIC' && (
                <TextLabel bold color="#FD812D" mb="4px">
                  This feedback is public and will be visible to everyone in the organisation
                </TextLabel>
              )}
              <TextLabel mb="4px" bold>
                Comments
              </TextLabel>
              {/* @ts-ignore */}
              <FeedbackRTE
                placeholder="Type your feedback here (Use # to include competencies)"
                parentRef={parentRef}
                editorState={feedbackPost}
                updateEditorState={onUpdateEditorState}
                setImage={setImage}
                setGif={setGif}
                uploadedImage={attachments?.images?.length ? attachments?.images[0] : null}
                uploadedGif={attachments?.gifs?.length ? attachments?.gifs[0] : null}
                onDeleteImage={onDeleteImage}
                useMentionFunctionality={false}
              />
              {checkCFGenAIEnabled() && (
                <Box pos="absolute" right={2} bottom={0}>
                  <Tooltip placement="bottom-end" label={helpMeWriteTooltip.tooltip}>
                    <MavenButton
                      // @ts-ignore
                      text="Help me write"
                      isDisabled={helpMeWriteTooltip.isDisabled}
                      onClick={handleHelpMeWrite}
                    />
                  </Tooltip>
                </Box>
              )}
            </Box>
            <Flex
              mt="4px"
              p="8px 0px"
              flexDir="column"
              bg="#FBFCFD"
              borderRadius="8px"
              pointerEvents={isNominated ? 'none' : 'all'}
              opacity={isNominated ? '0.08' : 1}
            >
              <Flex p="4px" alignItems="center">
                <RabbitCompetency boxSize="20px" fill="transparent" />
                <TextLabel bold>Competency</TextLabel>
              </Flex>
              <FeedbackCompetencies
                editorState={feedbackPost}
                setApplicableEmployeeIds={() => {}}
                setCompetency={setCompetency}
              />
            </Flex>
          </Box>
        </Flex>
        <Box ref={parentRef} />
      </Box>
    </Layout>
  );
};

export default RequestedFeedback;
